import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";

import { LktImpersonationBarComponent } from "@frontends-v3/design-system";

import { UserProfilesResponse } from "../../models/identity/responses/user-profiles-response.interface";
import { ProfileConstants } from "../../constants/profile.constants";
import { LektoAuthService } from "../../services/essential/lekto-auth.service";
import { ImpersonationService } from "../../services/identity/impersonation.service";

/**
 * Container da barra de personificação (DEV-742). Só renderiza a barra quando
 * há sessão personificada ativa; monta os dados (nome, perfil, contexto, nome do
 * admin) a partir do ImpersonationService + sessão atual, e trata o "Voltar".
 * Montado nos shells do admin e da plataforma.
 */
@Component({
  selector: "app-impersonation-bar",
  standalone: true,
  imports: [LktImpersonationBarComponent],
  templateUrl: "./impersonation-bar.container.html",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImpersonationBarContainerComponent {
  private readonly impersonationService = inject(ImpersonationService);
  private readonly authService = inject(LektoAuthService);

  public readonly testId = input<string>("impersonation-bar");

  public readonly isImpersonating = this.impersonationService.isImpersonating;
  public readonly impersonatedName = this.impersonationService.impersonatedUserName;
  public readonly adminName = this.impersonationService.originalAdminName;

  // CA06: `authProfile` é setado no createProfileCookie da troca de perfil, então
  // os computed reagem à troca durante a personificação (perfil/contexto do role
  // cookie são relidos) — sem depender de recarregar a página.
  public readonly profileLabel = computed<string>(() => {
    this.authService.authProfile();
    if (!this.isImpersonating()) return "";
    const profile = this.safeProfileData();
    if (!profile?.coRole) return "";
    return ProfileConstants.getProfileLabel(profile.coRole, profile.txProfileType || "Usuário");
  });

  public readonly context = computed<string>(() => {
    this.authService.authProfile();
    if (!this.isImpersonating()) return "";
    const profile = this.safeProfileData();
    return (
      profile?.txSchoolName ||
      profile?.txNetworkName ||
      this.authService.getUserInfoFromToken()?.context ||
      ""
    );
  });

  // O encerramento passou a ser feito pelo servidor (CA07) e por isso é assíncrono. Nada a esperar
  // aqui: ele termina em navegação de topo em qualquer desfecho — restauração ou logout — e nunca
  // rejeita, então não há erro para a barra tratar.
  public onExit(): void {
    void this.impersonationService.endImpersonation();
  }

  private safeProfileData(): UserProfilesResponse | null {
    try {
      return this.authService.getProfileData();
    } catch {
      return null;
    }
  }
}
